/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { ChevronDown, CirclePlus } from 'lucide-react';
import { useNavigate } from 'react-router';
import { debounce } from 'lodash';
import LayoutAdmin from '../../components/layouts/LayoutAdmin';
import { columns } from './Column';
import Input from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import useReceipt from '../../stores/useReceipt';
import Skeleton from '../../components/ui/skeleton';

export default function ReceiptPage() {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const { receipts, fetchReceipt, resetReceipts, loading, paginationData, cancelFetchReceipt } = useReceipt();
  const navigate = useNavigate();

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalPages: 0,
    totalCount: 0,
  });

  // Search filter state
  const [searchFilter, setSearchFilter] = useState('');

  // Fetch data function with params
  const fetchData = useCallback(() => {
    cancelFetchReceipt();

    fetchReceipt({
      page: pagination.pageIndex + 1, // API expects 1-based index
      pageSize: pagination.pageSize,
      filters: searchFilter ? { receipt_number: searchFilter } : undefined,
      sorting:
        sorting.length > 0
          ? {
              field: sorting[0].id,
              order: sorting[0].desc ? 'desc' : 'asc',
            }
          : undefined,
    });
  }, [cancelFetchReceipt, fetchReceipt, pagination.pageIndex, pagination.pageSize, searchFilter, sorting]);

  useEffect(() => {
    resetReceipts();
    fetchData();

    return () => {
      cancelFetchReceipt();
    };
  }, [pagination.pageIndex, pagination.pageSize, fetchData, searchFilter, sorting, cancelFetchReceipt, resetReceipts]);

  const table = useReactTable({
    data: receipts || [],
    columns,
    onSortingChange: (newSorting) => {
      setSorting(newSorting);
      cancelFetchReceipt();
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    manualSorting: true,
    pageCount: pagination.totalPages,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
  });

  useEffect(() => {
    resetReceipts();
    fetchData();
    return () => {
      cancelFetchReceipt();
    };
  }, [resetReceipts, fetchData, pagination.pageIndex, pagination.pageSize, searchFilter, sorting, cancelFetchReceipt]);

  // Update pagination state when data changes
  useEffect(() => {
    if (paginationData) {
      setPagination((prev) => ({
        ...prev,
        totalPages: paginationData.totalPages,
        totalCount: paginationData.totalCount,
      }));
    }
  }, [paginationData]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchFilter(value);
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0, // Reset to first page on new search
      }));
    }, 500), // 500ms delay
    [],
  );

  // Handle input change
  const handleSearch = (value: string) => {
    setSearchFilter(value); // Update the displayed input value immediately

    cancelFetchReceipt();

    debouncedSearch(value);
  };

  const handleNextPage = () => {
    if (pagination.pageIndex < pagination.totalPages - 1) {
      cancelFetchReceipt();

      setPagination((prev) => ({
        ...prev,
        pageIndex: prev.pageIndex + 1,
      }));
    }
  };

  const handlePreviousPage = () => {
    if (pagination.pageIndex > 0) {
      cancelFetchReceipt();

      setPagination((prev) => ({
        ...prev,
        pageIndex: prev.pageIndex - 1,
      }));
    }
  };

  // Cleanup debounced functions when component unmounts
  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  return (
    <LayoutAdmin header="Receipt">
      <div className="w-full">
        <div className="flex flex-col md:flex-row gap-2 items-center py-4">
          <Input
            placeholder="Search by Receipt No."
            value={searchFilter}
            onChange={(event) => handleSearch(event.target.value)}
            className="max-w-sm"
          />
          <div className="ml-auto flex gap-2">
            <Button variant="default" onClick={() => navigate('/receipt/create')}>
              <CirclePlus size={16} />
              <span>Create New Receipt</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  Columns <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="rounded-md border w-[calc(100dvw-32px)] md:w-full">
          <Table className="overflow-x-scroll">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map(
                  (
                    _,
                    rowIndex, // Ubah 5 sesuai jumlah row skeleton yang diinginkan
                  ) => (
                    <TableRow key={rowIndex}>
                      {columns.map((column, colIndex) => (
                        <TableCell key={colIndex}>
                          <Skeleton className="w-full h-6 rounded-md" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ),
                )
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between space-x-2 py-4">
          <div className="text-sm text-muted-foreground">
            Showing {pagination.pageIndex * pagination.pageSize + 1} to{' '}
            {Math.min((pagination.pageIndex + 1) * pagination.pageSize, pagination.totalCount)} of{' '}
            {pagination.totalCount} entries
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm">
              Page {pagination.pageIndex + 1} of {pagination.totalPages}
            </span>
            <Button variant="outline" size="sm" onClick={handlePreviousPage} disabled={pagination.pageIndex === 0}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={pagination.pageIndex >= pagination.totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </LayoutAdmin>
  );
}
